import { useCustomerTicketsDataQuery } from "entities/customerTickets/queries/useCustomerTicketsDataQuery";
import { FilterKeys } from "entities/customerTickets/types";
import { debounce } from "lodash";
import { useEffect, useMemo } from "react";
import { useSearchBox, useInstantSearch } from "react-instantsearch";
import { Filters } from "../types";
import { createFilterString, createReadFilterString } from "./utils";

export const useCustomerTicketsData = (filteredValues: Filters) => {
  const { query, refine: refineSearch, clear } = useSearchBox();
  const { status } = useInstantSearch();

  useEffect(() => {
    return () => {
      clear();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filters = useMemo(() => {
    const filterStrings = Object.keys(filteredValues).map((key) => {
      if (key === FilterKeys.read) {
        return createReadFilterString(filteredValues[key]);
      }
      return createFilterString(key, filteredValues[key]);
    });

    return filterStrings.filter(Boolean).join(" AND ");
  }, [filteredValues]);

  const {
    data: response,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    moveTicketToTop,
    updateCustomerTicketStatus,
    updateTicketAdminUnreadMessagesCount,
  } = useCustomerTicketsDataQuery({
    filters,
    query,
  });

  const fullTicketsList = useMemo(() => {
    return response?.pages.flatMap((array) => array.hits) ?? [];
  }, [response?.pages]);

  // Normalized table list data
  const data = useMemo(
    () =>
      fullTicketsList.map((hit) => ({
        ...hit,
        id: hit.objectID,
      })),
    [fullTicketsList],
  );

  const onSearch = debounce((value) => {
    refineSearch(value);
  }, 500);

  const isLoading = status === "loading";

  return {
    searchData: {
      value: query,
      onChange: onSearch,
    },
    tableData: {
      data: data || [],
      fetchNextPage,
      isFetchingNextPage,
      hasNextPage,
      refetch,
    },
    moveTicketToTop,
    updateCustomerTicketStatus,
    updateTicketAdminUnreadMessagesCount,
    isLoading,
  };
};
